/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Collapse,
  Divider,
  Chip,
} from "@mui/material";
import Iconify from "src/components/iconify";
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Position,
  MarkerType,
  Handle,
  getBezierPath,
  EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const COLORS = {
  source:     { bg: '#FFF1F2', border: '#E11D48', chip: '#BE123C', text: '#9F1239' },
  direct:     { bg: '#FFF7ED', border: '#EA580C', chip: '#C2410C', text: '#9A3412' },
  downstream: { bg: '#EFF6FF', border: '#3B82F6', chip: '#2563EB', text: '#1D4ED8' },
  edge: {
    direct:     '#E11D48',
    downstream: '#3B82F6',
  },
};

const TOKEN = {
  radius: '10px',
  radiusLg: '14px',
  shadow: '0 4px 20px rgba(0,0,0,0.08)',
  shadowHover: '0 8px 28px rgba(0,0,0,0.14)',
  transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
};

// ─── Inject CSS Keyframes once ─────────────────────────────────────────────────
const ANIM_CSS = `
  @keyframes rf-dash-flow {
    from { stroke-dashoffset: 30; }
    to   { stroke-dashoffset: 0;  }
  }
  @keyframes rf-pulse-glow {
    0%, 100% { opacity: 1;    filter: drop-shadow(0 0 2px var(--ec, #E11D48)); }
    50%       { opacity: 0.75; filter: drop-shadow(0 0 8px var(--ec, #E11D48)); }
  }
  @keyframes rf-selected-throb {
    0%, 100% { filter: drop-shadow(0 0 5px var(--ec, #E11D48)); }
    50%       { filter: drop-shadow(0 0 14px var(--ec, #E11D48)); }
  }
  @keyframes rf-dash-fast {
    from { stroke-dashoffset: 20; }
    to   { stroke-dashoffset: 0;  }
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('rf-anim-styles')) {
  const el = document.createElement('style');
  el.id = 'rf-anim-styles';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

// ─── Custom Edge: Direct (solid + continuous pulse glow) ───────────────────────
const DirectEdge = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  data, selected, label,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const color = COLORS.edge.direct;
  const isSelected = selected || data?.isSelected;
  const markerId = `direct-arrow-${id}`;

  return (
    <>
      <defs>
        <marker
          id={markerId}
          markerWidth="12" markerHeight="12"
          refX="10" refY="3.5"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,7 L10,3.5 z" fill={color} />
        </marker>
      </defs>

      {/* Soft glow halo */}
      <path
        d={edgePath}
        fill="none"
        stroke={color}
        strokeWidth={isSelected ? 10 : 7}
        strokeOpacity={isSelected ? 0.18 : 0.1}
        style={{
          '--ec': color,
          animation: isSelected
            ? 'rf-selected-throb 1s ease-in-out infinite'
            : 'rf-pulse-glow 2.4s ease-in-out infinite',
        }}
      />

      {/* Main line — dashes flow when selected, solid pulse when not */}
      <path
        d={edgePath}
        fill="none"
        stroke={color}
        strokeWidth={isSelected ? 3 : 2.5}
        markerEnd={`url(#${markerId})`}
        strokeDasharray={isSelected ? '6 4' : 'none'}
        style={{
          '--ec': color,
          animation: isSelected
            ? 'rf-dash-fast 0.5s linear infinite'
            : 'rf-pulse-glow 2.4s ease-in-out infinite',
        }}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 10, color: '#64748b',
              background: 'rgba(255,255,255,0.9)',
              padding: '1px 6px', borderRadius: 4,
              pointerEvents: 'none',
              border: '1px solid #FEE2E2',
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// ─── Custom Edge: Downstream (flowing dashes, always animated) ─────────────────
const DownstreamEdge = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  data, selected, label,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const color = COLORS.edge.downstream;
  const isSelected = selected || data?.isSelected;
  const markerId = `ds-arrow-${id}`;

  return (
    <>
      <defs>
        <marker
          id={markerId}
          markerWidth="12" markerHeight="12"
          refX="10" refY="3.5"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,7 L10,3.5 z" fill={color} />
        </marker>
      </defs>

      {/* Faint track */}
      <path
        d={edgePath}
        fill="none"
        stroke={color}
        strokeWidth={isSelected ? 4 : 3}
        strokeOpacity={0.1}
      />

      {/* Flowing dashes — always animating, faster + glowing when selected */}
      <path
        d={edgePath}
        fill="none"
        stroke={color}
        strokeWidth={isSelected ? 2.5 : 2}
        strokeDasharray="10 6"
        markerEnd={`url(#${markerId})`}
        style={{
          '--ec': color,
          animation: `rf-dash-flow ${isSelected ? '0.55s' : '1.4s'} linear infinite`,
          filter: isSelected ? `drop-shadow(0 0 5px ${color})` : 'none',
        }}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 10, color: '#64748b',
              background: 'rgba(255,255,255,0.9)',
              padding: '1px 6px', borderRadius: 4,
              pointerEvents: 'none',
              border: '1px solid #DBEAFE',
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// ─── Custom Node ──────────────────────────────────────────────────────────────
function ConfirmationNodeComponent({ data }) {
  const { isSourceNode, isDirectNode, isSelected } = data;
  const scheme = isSourceNode ? COLORS.source : isDirectNode ? COLORS.direct : COLORS.downstream;

  return (
    <div style={{
      background: scheme.bg,
      borderRadius: TOKEN.radius,
      border: `${isSourceNode ? 2 : 1.5}px solid ${scheme.border}`,
      boxShadow: isSelected
        ? `0 0 0 3px ${scheme.border}55, ${TOKEN.shadow}`
        : TOKEN.shadow,
      minWidth: '130px',
      maxWidth: '190px',
      padding: '10px 14px',
      position: 'relative',
      opacity: isSelected ? 1 : 0.85,
      transition: TOKEN.transition,
      cursor: 'pointer',
    }}>
      <Handle type="source" position={Position.Right}
        style={{ background: scheme.border, width: 8, height: 8, border: '2px solid white' }} />
      <Handle type="target" position={Position.Left}
        style={{ background: scheme.border, width: 8, height: 8, border: '2px solid white' }} />

      <Typography sx={{
        fontSize: '11.5px', fontWeight: 700, color: '#0F172A',
        textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.35,
        mb: isSourceNode || isDirectNode ? 0.75 : 0,
      }}>
        {data.label}
      </Typography>

      {isSourceNode && (
        <Chip label="Output Source" size="small" sx={{
          fontSize: '9px', height: '16px',
          backgroundColor: scheme.chip, color: 'white',
          fontWeight: 700, letterSpacing: '0.02em',
          '& .MuiChip-label': { px: 0.75 },
        }} />
      )}
      {isDirectNode && !isSourceNode && (
        <Chip label="Direct" size="small" sx={{
          fontSize: '9px', height: '16px',
          backgroundColor: scheme.chip, color: 'white',
          fontWeight: 700,
          '& .MuiChip-label': { px: 0.75 },
        }} />
      )}
    </div>
  );
}

// ─── ReactFlow type registries ─────────────────────────────────────────────────
const nodeTypes = { confirmationNode: ConfirmationNodeComponent };
const edgeTypes  = { directEdge: DirectEdge, downstreamEdge: DownstreamEdge };

// ─── Helpers ───────────────────────────────────────────────────────────────────
const LegendRow = ({ children, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
    {icon}
    <Typography variant="caption" sx={{ color: '#475569', fontSize: '11px' }}>{children}</Typography>
  </Box>
);

const SectionLabel = ({ color, children }) => (
  <Typography sx={{
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color, mb: 1,
  }}>
    {children}
  </Typography>
);

const DependencyItem = ({ checked, onChange, primary, secondary, accentColor }) => (
  <ListItem disableGutters sx={{
    px: 1.5, py: 0.75,
    borderRadius: '8px', mb: 0.5,
    bgcolor: checked ? `${accentColor}0D` : 'transparent',
    border: `1px solid ${checked ? accentColor + '44' : 'transparent'}`,
    transition: TOKEN.transition,
    '&:hover': { bgcolor: `${accentColor}0A` },
  }}>
    <Checkbox
      checked={checked}
      onChange={onChange}
      size="small"
      sx={{ color: accentColor + '88', '&.Mui-checked': { color: accentColor }, p: 0.5, mr: 1 }}
    />
    <ListItemText
      primary={<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1E293B' }}>{primary}</Typography>}
      secondary={secondary && <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.25 }}>{secondary}</Typography>}
    />
  </ListItem>
);

// ─── Main Dialog ──────────────────────────────────────────────────────────────
const OutputDeleteConfirmationDialog = ({
  open, onClose, onConfirm,
  directLinks = [], downstreamLinks = [],
  directNodes = [], downstreamNodes = [],
  selectedLinks = {}, setSelectedLinks,
  selectedNodes = {}, setSelectedNodes,
  setdeleteeditDialog,
}) => {
  const {
    filteredDirectLinks, filteredDownstreamLinks,
    filteredDirectNodes, filteredDownstreamNodes,
  } = useMemo(() => {
    if (!open) return {
      filteredDirectLinks: [], filteredDownstreamLinks: [],
      filteredDirectNodes: [], filteredDownstreamNodes: [],
    };
    const allNodeIds = new Set([...directNodes, ...downstreamNodes].map(n => n.id));
    return {
      filteredDirectLinks:     directLinks.filter(l => allNodeIds.has(l.source) && allNodeIds.has(l.target)),
      filteredDownstreamLinks: downstreamLinks.filter(l => allNodeIds.has(l.source) && allNodeIds.has(l.target)),
      filteredDirectNodes:     directNodes,
      filteredDownstreamNodes: downstreamNodes,
    };
  }, [open, directLinks, downstreamLinks, directNodes, downstreamNodes]);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowWrapper = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  useEffect(() => {
    if (open) {
      console.log('📊 OutputDeleteConfirmationDialog - Props received (filtered):', {
        directLinks: filteredDirectLinks.length,
        downstreamLinks: filteredDownstreamLinks.length,
        directNodes: filteredDirectNodes.length,
        downstreamNodes: filteredDownstreamNodes.length,
      });
    }
  }, [open, filteredDirectLinks, filteredDownstreamLinks, filteredDirectNodes, filteredDownstreamNodes]);

  useEffect(() => {
    if (!open) return;

    const allNodes = [...filteredDirectNodes, ...filteredDownstreamNodes];
    const allLinks  = [...filteredDirectLinks, ...filteredDownstreamLinks];

    if (allNodes.length === 0) {
      setRfNodes([{
        id: 'placeholder', type: 'confirmationNode',
        position: { x: 400, y: 300 },
        data: { label: 'No connections found', isSourceNode: false, isDirectNode: false },
      }]);
      setRfEdges([]);
      return;
    }

    const nodes = allNodes.map((node, index) => {
      const position = (node.position && typeof node.position.x === 'number')
        ? node.position
        : { x: 300 + (index % 3) * 250, y: 200 + Math.floor(index / 3) * 150 };

      return {
        id: node.id,
        type: 'confirmationNode',
        position,
        data: {
          label:        node.isSource ? `${node.name} (Output Source)` : node.name,
          isSourceNode: node.isSource || false,
          isDirectNode: filteredDirectNodes.some(n => n.id === node.id && !n.isSource),
          isSelected:   selectedNodes[node.id] || false,
        },
      };
    });

    const edges = allLinks.map(link => {
      const sourceExists = nodes.some(n => n.id === link.source);
      const targetExists = nodes.some(n => n.id === link.target);
      if (!sourceExists || !targetExists) {
        console.warn('Skipping link — nodes not found:', link);
        return null;
      }
      const isDirectLink = filteredDirectLinks.some(l => l.id === link.id);
      const isSelected   = selectedLinks[link.id] || false;

      return {
        id:     link.id,
        source: link.source,
        target: link.target,
        // ✅ Use our custom animated edge types
        type:   isDirectLink ? 'directEdge' : 'downstreamEdge',
        label:  link.label,
        data: { isDirectLink, originalId: link.id, isSelected },
        selected: isSelected,
      };
    }).filter(Boolean);

    setRfNodes(nodes);
    setRfEdges(edges);
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2 }), 100);
  }, [
    open,
    filteredDirectNodes, filteredDownstreamNodes,
    filteredDirectLinks, filteredDownstreamLinks,
    selectedNodes, selectedLinks,
    reactFlowInstance,
  ]);

  const handleClearAll = useCallback(() => {
    setSelectedLinks({});
    setSelectedNodes({});
  }, [setSelectedLinks, setSelectedNodes]);

  useEffect(() => { if (open) handleClearAll(); }, [open]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNodes(prev => ({ ...prev, [node.id]: !prev[node.id] }));
  }, [selectedNodes, setSelectedNodes]);

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedLinks(prev => ({ ...prev, [edge.id]: !prev[edge.id] }));
  }, [selectedLinks, setSelectedLinks]);

  const selectedLinksCount = useMemo(() =>
    Object.values(selectedLinks).filter(Boolean).length, [selectedLinks]);
  const selectedNodesCount = useMemo(() =>
    Object.values(selectedNodes).filter(Boolean).length, [selectedNodes]);
  const totalSelected = selectedLinksCount + selectedNodesCount;

  const getSummaryText = () => {
    const dc = filteredDirectLinks.length,    uc = filteredDownstreamLinks.length;
    const dn = filteredDirectNodes.length,    un = filteredDownstreamNodes.length;
    if (!dc && !uc && !dn && !un) return 'No dependencies found.';
    const parts = [];
    if (dc) parts.push(`${dc} direct connection${dc !== 1 ? 's' : ''}`);
    if (uc) parts.push(`${uc} downstream connection${uc !== 1 ? 's' : ''}`);
    if (dn) parts.push(`${dn} direct node${dn !== 1 ? 's' : ''}`);
    if (un) parts.push(`${un} downstream node${un !== 1 ? 's' : ''}`);
    return `Found ${parts.join(', ')} that will be affected.`;
  };

  // Keep node/edge data fresh without triggering full graph rebuild
  const liveNodes = useMemo(() =>
    rfNodes.map(n => ({
      ...n,
      data:     { ...n.data, isSelected: selectedNodes[n.id] || false },
      selected: selectedNodes[n.id] || false,
    })), [rfNodes, selectedNodes]);

  const liveEdges = useMemo(() =>
    rfEdges.map(e => ({
      ...e,
      selected: selectedLinks[e.id] || false,
      data:     { ...e.data, isSelected: selectedLinks[e.id] || false },
    })), [rfEdges, selectedLinks]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          border: '1px solid #F1F5F9',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        color: 'white', px: 3.5, py: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            background: 'rgba(239,68,68,0.2)',
            border: '1px solid rgba(239,68,68,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Iconify icon="eva:trash-2-fill" width={18} sx={{ color: '#FCA5A5' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
              Delete Output
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#94A3B8', mt: 0.25 }}>
              Dependencies detected — review before proceeding
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{
          color: '#94A3B8',
          '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
          borderRadius: '8px',
        }}>
          <Iconify icon="eva:close-fill" width={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pb: 3, px: 3.5, pt: 3, bgcolor: '#FAFBFC', borderColor: '#E2E8F0' }}>
        {/* ── Summary Banner ── */}
        <Box sx={{
          bgcolor: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: '10px', px: 2.5, py: 1.5, mb: 3,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Iconify icon="eva:alert-circle-fill" width={18} sx={{ color: '#E11D48', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '13.5px', color: '#9F1239', fontWeight: 500 }}>
            {getSummaryText()} Select which dependencies to delete.
          </Typography>
        </Box>

        {/* ── Graph ── */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Iconify icon="eva:globe-2-outline" width={16} sx={{ color: '#64748B' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
              Connection Graph
            </Typography>
            <Chip label="Click nodes or edges to select" size="small" sx={{
              fontSize: '10px', height: '18px', bgcolor: '#F1F5F9',
              color: '#64748B', fontWeight: 500,
              '& .MuiChip-label': { px: 1 },
            }} />
          </Box>

          <Box
            ref={reactFlowWrapper}
            sx={{
              height: 420,
              borderRadius: TOKEN.radiusLg,
              overflow: 'hidden',
              position: 'relative',
              border: '1.5px solid #E2E8F0',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.04)',
              bgcolor: '#F8FAFC',
            }}
          >
            <ReactFlow
              nodes={liveNodes}
              edges={liveEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onInit={setReactFlowInstance}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              minZoom={0.1}
              maxZoom={1.5}
              selectNodesOnDrag={false}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
            >
              <Background color="#CBD5E1" gap={24} size={1} />
              <Controls showInteractive={false} />
            </ReactFlow>

            {/* Zoom Controls */}
            <Box sx={{
              position: 'absolute', bottom: 14, right: 14,
              display: 'flex', flexDirection: 'column', gap: 0.75, zIndex: 1000,
            }}>
              {[
                { icon: 'eva:plus-fill',   action: () => reactFlowInstance?.zoomIn() },
                { icon: 'eva:minus-fill',  action: () => reactFlowInstance?.zoomOut() },
                { icon: 'eva:expand-fill', action: () => reactFlowInstance?.fitView({ padding: 0.2 }) },
              ].map(({ icon, action }, i) => (
                <IconButton key={i} onClick={action} size="small" sx={{
                  bgcolor: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px', width: 30, height: 30,
                  '&:hover': { bgcolor: '#F8FAFC', boxShadow: TOKEN.shadowHover },
                  transition: TOKEN.transition,
                }}>
                  <Iconify icon={icon} width={16} sx={{ color: '#475569' }} />
                </IconButton>
              ))}
            </Box>

            {/* Legend */}
            <Box sx={{
              position: 'absolute', top: 12, right: 12,
              bgcolor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(8px)',
              p: 1.75, borderRadius: TOKEN.radius,
              border: '1px solid #E2E8F0',
              boxShadow: TOKEN.shadow,
              zIndex: 1000, minWidth: 185,
            }}>
              <Typography sx={{
                fontSize: '11px', fontWeight: 700, color: '#0F172A',
                mb: 1.25, letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                Legend
              </Typography>

              <LegendRow icon={
                <Box sx={{ width: 28, height: 3, bgcolor: COLORS.edge.direct, borderRadius: 1, flexShrink: 0,
                  animation: 'rf-pulse-glow 2.4s ease-in-out infinite', '--ec': COLORS.edge.direct }} />
              }>
                Direct — pulse glow
              </LegendRow>

              <LegendRow icon={
                <Box component="svg" width={28} height={8} sx={{ flexShrink: 0, overflow: 'visible' }}>
                  <line x1="0" y1="4" x2="28" y2="4"
                    stroke={COLORS.edge.downstream} strokeWidth="2.5"
                    strokeDasharray="6 4"
                    style={{ animation: 'rf-dash-flow 1.4s linear infinite' }}
                  />
                </Box>
              }>
                Downstream — flowing
              </LegendRow>

              {[
                { color: COLORS.source,     label: 'Output Source' },
                { color: COLORS.direct,     label: 'Direct Node' },
                { color: COLORS.downstream, label: 'Downstream Node' },
              ].map(({ color, label }) => (
                <LegendRow key={label} icon={
                  <Box sx={{
                    width: 11, height: 11, borderRadius: '50%',
                    bgcolor: color.bg, border: `2px solid ${color.border}`, flexShrink: 0,
                  }} />
                }>{label}</LegendRow>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#E2E8F0' }} />

        {/* ── Dependency Lists ── */}
        <Box sx={{ mt: 3 }}>
          {filteredDirectLinks.length > 0 && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'white', borderRadius: TOKEN.radius, border: '1px solid #FEE2E2' }}>
              <SectionLabel color={COLORS.source.text}>
                Direct Connections ({filteredDirectLinks.length})
              </SectionLabel>
              <List dense disablePadding>
                {filteredDirectLinks.map(link => (
                  <DependencyItem
                    key={link.id}
                    checked={!!selectedLinks[link.id]}
                    onChange={() => setSelectedLinks(prev => ({ ...prev, [link.id]: !prev[link.id] }))}
                    primary={`${link.sourceName || 'Unknown'} → ${link.targetName || 'Unknown'}`}
                    secondary={link.label}
                    accentColor={COLORS.edge.direct}
                  />
                ))}
              </List>
            </Box>
          )}

          {filteredDirectNodes.length > 0 && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'white', borderRadius: TOKEN.radius, border: '1px solid #FFEDD5' }}>
              <SectionLabel color={COLORS.direct.text}>
                Direct Nodes ({filteredDirectNodes.length})
              </SectionLabel>
              <List dense disablePadding>
                {filteredDirectNodes.map(node => (
                  <DependencyItem
                    key={node.id}
                    checked={!!selectedNodes[node.id]}
                    onChange={() => setSelectedNodes(prev => ({ ...prev, [node.id]: !prev[node.id] }))}
                    primary={node.name}
                    secondary={node.isSource ? 'Output Source' : 'Directly connected'}
                    accentColor={COLORS.direct.border}
                  />
                ))}
              </List>
            </Box>
          )}

          {(filteredDownstreamLinks.length > 0 || filteredDownstreamNodes.length > 0) && (
            <Box sx={{ bgcolor: 'white', borderRadius: TOKEN.radius, border: '1px solid #DBEAFE', overflow: 'hidden' }}>
              <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 1.5, cursor: 'pointer',
                  bgcolor: expanded ? '#EFF6FF' : 'white',
                  transition: TOKEN.transition,
                  '&:hover': { bgcolor: '#EFF6FF' },
                }}
              >
                <Box sx={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: TOKEN.transition, display: 'flex' }}>
                  <Iconify icon="eva:arrow-ios-downward-fill" width={16} sx={{ color: COLORS.downstream.text }} />
                </Box>
                <SectionLabel color={COLORS.downstream.text}>
                  Downstream ({filteredDownstreamLinks.length} connections, {filteredDownstreamNodes.length} nodes)
                </SectionLabel>
              </Box>

              <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <List dense disablePadding>
                    {filteredDownstreamLinks.map(link => (
                      <DependencyItem
                        key={link.id}
                        checked={!!selectedLinks[link.id]}
                        onChange={() => setSelectedLinks(prev => ({ ...prev, [link.id]: !prev[link.id] }))}
                        primary={`${link.sourceName} → ${link.targetName}`}
                        secondary={link.label}
                        accentColor={COLORS.edge.downstream}
                      />
                    ))}
                    {filteredDownstreamNodes.map(node => (
                      <DependencyItem
                        key={node.id}
                        checked={!!selectedNodes[node.id]}
                        onChange={() => setSelectedNodes(prev => ({ ...prev, [node.id]: !prev[node.id] }))}
                        primary={node.name}
                        secondary="Downstream node"
                        accentColor={COLORS.edge.downstream}
                      />
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* ── Selection Summary Bar ── */}
        <Box sx={{
          mt: 3, bgcolor: 'white',
          border: '1.5px solid #E2E8F0',
          borderRadius: TOKEN.radius,
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Iconify
              icon="eva:checkmark-circle-2-outline"
              width={18}
              sx={{ color: totalSelected > 0 ? '#10B981' : '#CBD5E1' }}
            />
            <Typography sx={{ fontSize: '13px', color: '#374151' }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>{totalSelected}</Box>
              {' '}item{totalSelected !== 1 ? 's' : ''} selected
              {selectedLinksCount > 0 && ` · ${selectedLinksCount} connection${selectedLinksCount !== 1 ? 's' : ''}`}
              {selectedNodesCount > 0 && ` · ${selectedNodesCount} node${selectedNodesCount !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleClearAll}
            disabled={totalSelected === 0}
            sx={{
              fontSize: '12px', fontWeight: 600,
              color: '#64748B', px: 1.5, py: 0.5, borderRadius: '6px',
              '&:hover': { bgcolor: '#F1F5F9', color: '#374151' },
            }}
          >
            Clear All
          </Button>
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3.5, py: 2.5, bgcolor: 'white', borderTop: '1px solid #E2E8F0', gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '9px', borderColor: '#D1D5DB',
            color: '#374151', fontWeight: 600, px: 2.5,
            '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (typeof setdeleteeditDialog === 'function') setdeleteeditDialog(true);
            if (typeof onConfirm === 'function') onConfirm({ selectedLinks, selectedNodes });
          }}
          variant="contained"
          disabled={totalSelected === 0}
          startIcon={<Iconify icon="eva:trash-2-fill" width={16} />}
          sx={{
            borderRadius: '9px', fontWeight: 700, px: 2.5,
            background: totalSelected > 0
              ? 'linear-gradient(135deg, #E11D48 0%, #BE123C 100%)'
              : undefined,
            boxShadow: totalSelected > 0 ? '0 4px 12px rgba(225,29,72,0.35)' : undefined,
            '&:hover': {
              background: 'linear-gradient(135deg, #BE123C 0%, #9F1239 100%)',
              boxShadow: '0 6px 16px rgba(225,29,72,0.45)',
            },
            transition: TOKEN.transition,
          }}
        >
          Delete Selected ({totalSelected})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function OutputDeleteConfirmationDialogWrapper(props) {
  return (
    <ReactFlowProvider>
      <OutputDeleteConfirmationDialog {...props} />
    </ReactFlowProvider>
  );
}